import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getApiBaseUrl, getStoredToken } from "../api/client";
import { useOrg } from "./OrgContext";
import { peekZktecoPairingCode, takeZktecoPairingCode } from "../lib/zkteco-pending-pair";
import {
  configureLocalEngine,
  detectDeviceClass,
  type DeviceClass,
  type HardwareFeature,
  hardwareBlockedOnMobile,
  hardwareRequiresLocalEngine,
  discoverLocalEngineBaseUrl,
  LOCAL_ENGINE_DEFAULT_URL,
  pingLocalEngine,
  type LocalEngineHealth,
  type LocalEngineStatus,
  fetchLocalEngineStatus,
} from "../lib/local-engine";

type GateState = {
  open: boolean;
  feature: HardwareFeature | null;
  reason: "mobile" | "engine_offline" | null;
};

interface LocalEngineContextValue {
  baseUrl: string;
  deviceClass: DeviceClass;
  isMobile: boolean;
  isDesktop: boolean;
  health: LocalEngineHealth | null;
  status: LocalEngineStatus | null;
  online: boolean;
  checking: boolean;
  refresh: (opts?: { forceDiscover?: boolean }) => Promise<void>;
  /** Returns true if action may proceed (desktop + engine online, or non-hardware). */
  ensureHardware: (feature: HardwareFeature) => boolean;
  gate: GateState;
  closeGate: () => void;
}

const LocalEngineContext = createContext<LocalEngineContextValue | null>(null);

/** Avoid localhost port scans on every ERP page — HR only needs helper on hardware routes. */
function pathWantsLocalEngine(): boolean {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname;
  return (
    p.includes("/hr/attendance") ||
    p.includes("/zkteco") ||
    p.includes("/settings/local-engine") ||
    p.startsWith("/pos")
  );
}

export function LocalEngineProvider({ children }: { children: ReactNode }) {
  const { orgId } = useOrg();
  const [baseUrl, setBaseUrl] = useState(LOCAL_ENGINE_DEFAULT_URL);
  const deviceClass = useMemo(() => detectDeviceClass(), []);
  const isMobile = deviceClass === "mobile";
  const isDesktop = deviceClass === "desktop";

  const [health, setHealth] = useState<LocalEngineHealth | null>(null);
  const [status, setStatus] = useState<LocalEngineStatus | null>(null);
  const [checking, setChecking] = useState(true);
  const [gate, setGate] = useState<GateState>({ open: false, feature: null, reason: null });

  const online = Boolean(health?.ok);

  const refresh = useCallback(async (opts?: { forceDiscover?: boolean }) => {
    setChecking(true);
    try {
      let url = baseUrl;
      let h = await pingLocalEngine(url);
      if (!h) {
        url = await discoverLocalEngineBaseUrl({ force: opts?.forceDiscover });
        if (url !== baseUrl) setBaseUrl(url);
        if (url !== baseUrl || opts?.forceDiscover) {
          h = await pingLocalEngine(url);
        }
      }
      setHealth(h);
      if (h) {
        const token = getStoredToken();
        const api = getApiBaseUrl();
        if (token && api) {
          const pairingCode = peekZktecoPairingCode();
          await configureLocalEngine(
            {
              apiBaseUrl: api,
              accessToken: token,
              orgId: orgId ?? undefined,
              pairingCode: pairingCode ?? undefined,
            },
            url,
          )
            .then((res) => {
              if (pairingCode && res.ok) takeZktecoPairingCode();
            })
            .catch(() => undefined);
        }
        try {
          const s = await fetchLocalEngineStatus(url);
          setStatus(s);
        } catch {
          setStatus(null);
        }
      } else {
        setStatus(null);
      }
    } finally {
      setChecking(false);
    }
  }, [baseUrl, orgId]);

  useEffect(() => {
    if (!getStoredToken() || !pathWantsLocalEngine()) {
      setChecking(false);
      return;
    }
    void discoverLocalEngineBaseUrl({ force: true }).then(setBaseUrl);
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setChecking(false);
      return;
    }

    const tick = () => {
      if (!getStoredToken()) return;
      if (!pathWantsLocalEngine()) {
        setChecking(false);
        return;
      }
      void refresh();
    };

    void tick();
    const id = setInterval(tick, 8000);
    return () => clearInterval(id);
  }, [refresh]);

  const closeGate = useCallback(() => {
    setGate({ open: false, feature: null, reason: null });
  }, []);

  const ensureHardware = useCallback(
    (feature: HardwareFeature): boolean => {
      if (!hardwareRequiresLocalEngine(feature)) return true;

      if (hardwareBlockedOnMobile(feature) && isMobile) {
        setGate({ open: true, feature, reason: "mobile" });
        return false;
      }

      if (!online) {
        setGate({ open: true, feature, reason: "engine_offline" });
        return false;
      }

      return true;
    },
    [isMobile, online],
  );

  const value = useMemo(
    () => ({
      baseUrl,
      deviceClass,
      isMobile,
      isDesktop,
      health,
      status,
      online,
      checking,
      refresh,
      ensureHardware,
      gate,
      closeGate,
    }),
    [
      baseUrl,
      deviceClass,
      isMobile,
      isDesktop,
      health,
      status,
      online,
      checking,
      refresh,
      ensureHardware,
      gate,
      closeGate,
    ],
  );

  return <LocalEngineContext.Provider value={value}>{children}</LocalEngineContext.Provider>;
}

export function useLocalEngine(): LocalEngineContextValue {
  const ctx = useContext(LocalEngineContext);
  if (!ctx) {
    throw new Error("useLocalEngine must be used within LocalEngineProvider");
  }
  return ctx;
}
